/**
 * MMZ Cart Upsells
 * Custom element gérant les upsells AJAX dans le cart drawer Pitch.
 *
 * Cycle de vie :
 *  - connectedCallback : init buttons, nav carousel, sync état panier
 *  - Après un morphSection de Pitch, l'élément est remplacé → connectedCallback
 *    se ré-exécute automatiquement sur le nouvel élément → état toujours cohérent.
 */
class MmzCartUpsells extends HTMLElement {
  connectedCallback() {
    this.#bindButtons();
    this.#bindNav();
    this.#syncCartState();
  }

  /* ── Boutons Ajouter ─────────────────────────────────────────── */

  #bindButtons() {
    this.querySelectorAll('.mmz-upsells__btn').forEach((btn) => {
      btn.addEventListener('click', this.#onAdd);
    });
  }

  #onAdd = async (e) => {
    const btn = /** @type {HTMLButtonElement} */ (e.currentTarget);
    if (btn.disabled) return;

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    const card = btn.closest('.mmz-upsells__card');
    const sectionId = this.dataset.sectionId;

    btn.disabled = true;
    btn.classList.add('mmz-upsells__btn--loading');

    try {
      const body = { id: Number(variantId), quantity: 1 };
      if (sectionId) body.sections = [sectionId];

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Erreur ajout panier');
      }

      const data = await res.json();

      this.#markAdded(card);

      /* Déclenche le morphSection de Pitch pour mettre à jour
         le panier (items + totaux) sans fermer le drawer. */
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: data,
            data: {
              sections: data.sections ?? {},
              itemCount: data.item_count,
            },
          },
        })
      );
    } catch {
      btn.disabled = false;
      btn.classList.remove('mmz-upsells__btn--loading');
    }
  };

  /* ── Sync état "déjà au panier" ──────────────────────────────── */

  async #syncCartState() {
    try {
      const cart = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
      }).then((r) => r.json());

      const inCart = new Set(cart.items.map((item) => String(item.variant_id)));

      this.querySelectorAll('.mmz-upsells__card').forEach((card) => {
        if (inCart.has(card.dataset.variantId)) {
          this.#markAdded(card);
        }
      });
    } catch {
      /* silent */
    }
  }

  /* ── Marquer une carte comme ajoutée ────────────────────────── */

  #markAdded(card) {
    if (!card) return;
    const btn = card.querySelector('.mmz-upsells__btn');
    if (!btn) return;
    const label = btn.querySelector('.mmz-upsells__btn-text');

    btn.disabled = true;
    btn.classList.remove('mmz-upsells__btn--loading');
    btn.classList.add('mmz-upsells__btn--added');
    if (label) label.textContent = 'Ajouté ✓';
  }

  /* ── Carousel navigation (slider uniquement) ────────────────── */

  #bindNav() {
    const track = this.querySelector('.mmz-upsells__track--slider');
    if (!track) return;

    const prevBtn = this.querySelector('.mmz-upsells__nav--prev');
    const nextBtn = this.querySelector('.mmz-upsells__nav--next');
    if (!prevBtn || !nextBtn) return;

    const cardWidth = () => {
      const card = track.querySelector('.mmz-upsells__card');
      return card ? card.offsetWidth + 10 : 160;
    };

    nextBtn.addEventListener('click', () => {
      track.scrollBy({ left: cardWidth(), behavior: 'smooth' });
    });
    prevBtn.addEventListener('click', () => {
      track.scrollBy({ left: -cardWidth(), behavior: 'smooth' });
    });

    track.addEventListener(
      'scroll',
      () => this.#updateNav(track, prevBtn, nextBtn),
      { passive: true }
    );

    this.#updateNav(track, prevBtn, nextBtn);
  }

  #updateNav(track, prevBtn, nextBtn) {
    prevBtn.hidden = track.scrollLeft <= 4;
    nextBtn.hidden = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
  }
}

customElements.define('mmz-cart-upsells', MmzCartUpsells);
